import React, { useState } from "react";
import { productEducationData } from "../data/productEducation";

// ✅ Import All Framework Components
import Timeline from "../components/framework/Timeline";
import Matrix from "../components/framework/Matrix";
import AssetModal from "../components/framework/AssetModal";
import SharePointMockup from "../components/framework/SharePointMockup";
import RoleJourneyModal from "../components/framework/RoleJourneyModal";
import SharePointContentPage from "../components/framework/SharePointContentPage"; // ✅ New Import

/* =========================
   PAGE-SPECIFIC STYLES 
   ========================= */
const PageStyles = () => (
  <style>{`
    :root {
      --brand: #7C3AED; 
      --brand-50: #F5F3FF;
      --text-main: #0f172a; 
      --text-muted: #64748b; 
      --bg-card: #ffffff;
      --line: #e2e8f0; 
    }
    
    .page-container { max-width: 1000px; margin: 0 auto; padding: 3rem 2rem; font-family: sans-serif; }
    
    h1 { font-size: 2.25rem; margin: 0 0 0.5rem 0; letter-spacing: -0.02em; color: var(--text-main); }
    h2 { font-size: 1.5rem; margin: 3rem 0 1.5rem; padding-bottom: 0.75rem; border-bottom: 1px solid var(--line); color: var(--text-main); }
    h3 { font-size: 1.25rem; margin: 0 0 1rem 0; color: var(--text-main); }
    h4 { color: var(--brand); margin: 0 0 0.5rem 0; font-size: 1.1rem; }
    p { line-height: 1.6; color: var(--text-muted); }
    
    .content-card { background: var(--bg-card); border: 1px solid var(--line); border-radius: 12px; box-shadow: 0 1px 2px rgba(0,0,0,0.05); padding: 1.5rem; margin-bottom: 1.5rem; }
    
    .badge { display: inline-flex; align-items: center; padding: 0.25rem 0.75rem; border-radius: 999px; font-size: 0.75rem; font-weight: 700; text-transform: uppercase; background: var(--brand-50); color: var(--brand); margin-bottom: 1rem; }
    
    .chip { border: 1px solid var(--line); border-radius: 999px; padding: 0.5rem 1rem; background: #fff; cursor: pointer; margin-right: 0.5rem; font-weight: 500; color: var(--text-muted); transition: all 0.2s; }
    .chip:hover { border-color: var(--brand); color: var(--brand); }
    .chip[aria-current="true"] { background: var(--brand); color: #fff; border-color: var(--brand); }
    
    .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 1.5rem; }
    .grid-auto { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 1.5rem; }
    
    table { width: 100%; border-collapse: collapse; }
    th { text-align: left; font-size: 0.85rem; color: var(--text-muted); text-transform: uppercase; padding-bottom: 0.5rem; border-bottom: 1px solid var(--line); }
    td { padding: 1rem 0; border-bottom: 1px solid var(--line); vertical-align: top; }
  `}</style>
);

export default function ProductEducationPage() {
  const meta = productEducationData?.meta || {};
  const strategy = productEducationData?.strategy || {};
  const timeline = productEducationData?.timeline || { phases: [] };
  const resources = productEducationData?.resources || {};
  const risks = productEducationData?.risks || { rows: [] };

  const [activeTab, setActiveTab] = useState("executive");
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [selectedRoleJourney, setSelectedRoleJourney] = useState(null);
  const [selectedPhaseContent, setSelectedPhaseContent] = useState(null); // ✅ New State for Phase Content

  const handleRoleSelect = (roleKey) => {
    const journeyData = strategy.roleJourneys?.[roleKey];
    if (journeyData) {
      setSelectedRoleJourney(journeyData);
    }
  };

  // ✅ New Handler: Opens the SharePoint Page
  const handlePhaseClick = (phase, roleName) => {
    setSelectedPhaseContent({
      phaseName: phase.name,
      role: roleName,
      items: phase.items
    });
  };

  return (
    <div className="page-container">
      <PageStyles />
      
      {/* HEADER */}
      <div>
        <span className="badge">Project Status: Phase 2 (Active)</span>
        <h1>{meta.title}</h1>
        <p style={{ fontSize: "1.25rem", maxWidth: "65ch", color: "var(--text-main)" }}>
          {meta.subtitle}
        </p>
      </div>

      {/* STRATEGY SECTION */}
      <section>
        <h2>Strategy: The 3x4 Matrix</h2>
        
        <div style={{ marginBottom: "1.5rem" }}>
          <button className="chip" onClick={() => setActiveTab("executive")} aria-current={activeTab === "executive"}>Executive View</button>
          <button className="chip" onClick={() => setActiveTab("matrix")} aria-current={activeTab === "matrix"}>Curriculum Matrix</button>
          <button className="chip" onClick={() => setActiveTab("vision")} aria-current={activeTab === "vision"}>Phase 3 Vision</button>
          <button className="chip" onClick={() => setActiveTab("personas")} aria-current={activeTab === "personas"}>Learner Personas</button>
          <button className="chip" onClick={() => setActiveTab("deliverables")} aria-current={activeTab === "deliverables"}>Deliverables</button>
        </div>

        <div className="content-card">
          {activeTab === "executive" && (
            <div>
              <div style={{ borderLeft: "4px solid var(--brand)", paddingLeft: "1.25rem", marginBottom: "1.5rem" }}>
                <h4 style={{ fontSize: "0.9rem", textTransform: "uppercase", letterSpacing: "1px" }}>{strategy.northStar?.label}</h4>
                <p style={{ margin: 0, fontWeight: "600", fontSize: "1.1rem", color: "var(--text-main)" }}>
                  {strategy.northStar?.statement}
                </p>
              </div>
              <p>{strategy.tabs?.executive?.body}</p>
              <div style={{ marginTop: "1rem", background: "#f8fafc", padding: "1rem", borderRadius: "8px" }}>
                <strong>Leading Indicators: </strong> {strategy.tabs?.executive?.proofSignals?.leading.join(", ")}
              </div>
            </div>
          )}

          {activeTab === "matrix" && (
            <div>
              <div style={{ marginBottom: "1.5rem" }}>
                <h4 style={{ margin: 0 }}>The Build List</h4>
                <p style={{ margin: 0, fontSize: "0.95rem" }}>
                  The intersection of Product Lines and Roles defines our 12 Core Assets for Phase 2.
                  <br/>
                  <span style={{color: "var(--brand)", fontWeight: "600", fontSize: "0.85rem"}}>✨ Click any asset to preview the prototype.</span>
                </p>
              </div>
              <Matrix 
                data={strategy.matrix} 
                onAssetClick={(asset) => setSelectedAsset(asset)} 
              />
            </div>
          )}

          {activeTab === "vision" && (
            <div>
              <div style={{ marginBottom: "1.5rem" }}>
                <h4 style={{ margin: 0 }}>The Future State</h4>
                <p style={{ margin: 0, fontSize: "0.95rem" }}>
                  This is the target architecture for the SharePoint site launch.
                  <br/>
                  <span style={{color: "var(--brand)", fontWeight: "600", fontSize: "0.85rem"}}>✨ Click a Role Card (AE, PS, CS) to see their unique Journey.</span>
                </p>
              </div>
              <SharePointMockup onRoleSelect={handleRoleSelect} />
            </div>
          )}

          {activeTab === "personas" && (
            <div className="grid-auto">
              {(strategy.tabs?.sages?.sages || []).map((persona, i) => (
                <div key={i}>
                  <h4>{persona.name}</h4>
                  <p style={{ fontSize: "0.85rem", fontStyle: "italic", marginBottom: "0.5rem" }}>{persona.philosophy}</p>
                  <ul style={{ paddingLeft: "1.2rem", margin: 0, fontSize: "0.9rem", color: "var(--text-muted)" }}>
                    {persona.bullets.map((b, j) => <li key={j} style={{ marginBottom: "0.25rem" }}>{b}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          )}

          {activeTab === "deliverables" && (
            <div className="grid-2">
               <div>
                 <h4>Core Artifacts</h4>
                 <ul style={{ paddingLeft: "1.2rem", color: "var(--text-muted)" }}>
                   {(strategy.tabs?.deliverables?.core || []).map((i, k) => <li key={k} style={{marginBottom:".5rem"}}>{i}</li>)}
                 </ul>
               </div>
               <div>
                 <h4>Certification</h4>
                 <ul style={{ paddingLeft: "1.2rem", color: "var(--text-muted)" }}>
                   {(strategy.tabs?.deliverables?.cert || []).map((i, k) => <li key={k} style={{marginBottom:".5rem"}}>{i}</li>)}
                 </ul>
               </div>
            </div>
          )}
        </div>
      </section>

      {/* TIMELINE SECTION */}
      <section>
        <h2>{timeline.heading}</h2>
        <Timeline items={timeline.phases} />
      </section>

      {/* RESOURCING & RISKS */}
      <section className="grid-2" style={{ marginTop: "3rem" }}>
        <div>
           <h3>{resources.heading}</h3>
           <div className="content-card">
              <h4 style={{ color: "var(--text-muted)", fontSize: "0.8rem", textTransform: "uppercase" }}>MVP Squad</h4>
              <ul style={{ paddingLeft: "1.2rem", marginBottom: "1.5rem" }}>
                {(resources.minimumViableTeam?.items || []).map(i => <li key={i}>{i}</li>)}
              </ul>
              <h4 style={{ color: "var(--brand)", fontSize: "0.8rem", textTransform: "uppercase" }}>Ideal State</h4>
              <ul style={{ paddingLeft: "1.2rem" }}>
                {(resources.idealTeam?.items || []).map(i => <li key={i}>{i}</li>)}
              </ul>
           </div>
        </div>
        <div>
          <h3>{risks.heading}</h3>
          <div className="content-card">
            <table>
              <thead>
                <tr><th>Risk</th><th>Mitigation</th></tr>
              </thead>
              <tbody>
                {risks.rows.map((r, i) => (
                  <tr key={i}>
                    <td style={{ color: "#ef4444", fontWeight: "600", width: "40%" }}>{r.risk}</td>
                    <td>{r.mitigation}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* MODAL LAYER CAKE */}
      
      {/* 1. Asset Modal */}
      {selectedAsset && (
        <AssetModal 
          asset={selectedAsset} 
          onClose={() => setSelectedAsset(null)} 
        />
      )}

      {/* 2. Role Journey Tour */}
      {selectedRoleJourney && (
        <RoleJourneyModal 
          roleData={selectedRoleJourney} 
          onClose={() => setSelectedRoleJourney(null)}
          onPhaseClick={handlePhaseClick} // ✅ Pass the click handler
        />
      )}

      {/* 3. SharePoint Content Page (The Big Ass Mockup) */}
      {selectedPhaseContent && (
        <SharePointContentPage 
          data={selectedPhaseContent}
          onClose={() => setSelectedPhaseContent(null)}
        />
      )}

    </div>
  );
}
